import { useAcceptDcmFiles } from './AcceptFilesFromBrowser.hooks';
import { File3dDropLoader } from './File3dDropLoader';
import { computeInitialScanReviewViewStateFromDcm } from './ScanReviewLandingPage.utils';
import {
    ScanReviewCompleteViewButtonsToolbar,
    ScanReviewCompleteViewToolButtonsToolbar,
    ScanReviewMarginMarkingViewToolbar,
} from './ScanReviewLandingPageToolbars';
import {
    type ScanReviewDcmFile,
    extractScanReviewDcmFileData,
    type ScanReviewViewState,
    ScanReviewPanelType,
    ScanReviewViewType,
    loadScanReviewDcmFileData,
    type ScanReviewRecordFactory,
    ScanReviewDisplayType,
    useScanReviewCompleteViewAppContext,
    type QcHeatmapRange,
    ScanReviewDcmBuilder,
    ScanReviewCompleteViewAppProvider,
    ScanReviewCompleteView,
    ScanReviewAppProvider,
    ScanReviewMarginMarkingView,
    useScanReviewMarginMarkingViewContext,
    ScanReviewMarginMarkingViewAppProvider,
    ScanReviewMarginMarkingTool,
    ScanReviewEditedMarginLine,
} from '@orthly/dentin';
import { AllToothNumbers, type ToothNumber, AllUpperToothNumbers, AllLowerToothNumbers } from '@orthly/items';
import { Jaw } from '@orthly/shared-types';
import { StackY, StackX } from '@orthly/ui';
import React from 'react';
import type * as THREE from 'three';

const INITIAL_CLEARANCE_RANGE: QcHeatmapRange = { min: -0.1, max: 1.0 };

const ScanReviewLandingPageCompleteView: React.VFC<{
    marginMarking: boolean;
    setMarginMarking: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ marginMarking, setMarginMarking }) => {
    const { scene, heatMapRange } = useScanReviewCompleteViewAppContext();
    return (
        <StackX sx={{ flex: 'auto', minHeight: 0 }}>
            <ScanReviewCompleteViewButtonsToolbar setMarginMarking={setMarginMarking} scene={scene} />
            <StackY sx={{ flex: 'auto', minHeight: 0 }}>
                <ScanReviewCompleteView />
                <ScanReviewCompleteViewToolButtonsToolbar
                    marginMarking={marginMarking}
                    setMarginMarking={setMarginMarking}
                    scene={scene}
                    heatMapRange={heatMapRange}
                />
            </StackY>
        </StackX>
    );
};

const ScanReviewLandingPageMarginMarkingView: React.VFC<{
    marginMarking: boolean;
    setMarginMarking: React.Dispatch<React.SetStateAction<boolean>>;
    marginLines: Map<ToothNumber, ScanReviewEditedMarginLine>;
    preppedTeeth: Map<ToothNumber, boolean>;
    setPreppedTeeth: React.Dispatch<React.SetStateAction<Map<ToothNumber, boolean>>>;
}> = ({ marginMarking, setMarginMarking, preppedTeeth, setPreppedTeeth }) => {
    const { scene, toolController } = useScanReviewMarginMarkingViewContext();
    return (
        <StackX sx={{ flex: 'auto', minHeight: 0 }} onContextMenu={e => e.preventDefault()}>
            <ScanReviewCompleteViewButtonsToolbar setMarginMarking={setMarginMarking} scene={scene} />
            <StackY sx={{ flex: 'auto', minHeight: 0, minWidth: 0 }}>
                <ScanReviewMarginMarkingView>
                    {toolController && <ScanReviewMarginMarkingTool toolController={toolController} />}
                </ScanReviewMarginMarkingView>
                <ScanReviewCompleteViewToolButtonsToolbar
                    marginMarking={marginMarking}
                    setMarginMarking={setMarginMarking}
                    scene={scene}
                    heatMapRange={null}
                />
            </StackY>
            <ScanReviewMarginMarkingViewToolbar preppedTeeth={preppedTeeth} setPreppedTeeth={setPreppedTeeth} />
        </StackX>
    );
};

export const ScanReviewLandingPage: React.VFC = () => {
    const [dcms, setDcms] = React.useState<ScanReviewDcmFile[]>([]);
    const { onDcmDrop } = useAcceptDcmFiles(setDcms);

    const [upperJawFactory, setUpperJawFactory] = React.useState<ScanReviewRecordFactory | null>(null);
    const [lowerJawFactory, setLowerJawFactory] = React.useState<ScanReviewRecordFactory | null>(null);

    const [initialViewState, setInitialViewState] = React.useState<ScanReviewViewState>({
        [ScanReviewPanelType.Upper]: null,
        [ScanReviewPanelType.Lower]: null,
        [ScanReviewPanelType.Left]: null,
        [ScanReviewPanelType.Right]: null,
        [ScanReviewPanelType.Front]: null,
    });

    const [preppedTeeth, setPreppedTeeth] = React.useState<Map<ToothNumber, boolean>>(
        new Map(AllToothNumbers.map(unn => [unn, false])),
    );

    const toothNumber = React.useMemo(
        () => [...preppedTeeth].filter(([_unn, prepped]) => prepped).map(([unn, _prepped]) => unn)[0],
        [preppedTeeth],
    );

    const marginLines = React.useMemo<Map<ToothNumber, ScanReviewEditedMarginLine>>(
        () => new Map(AllToothNumbers.map(unn => [unn, new ScanReviewEditedMarginLine(unn, [], false)])),
        [],
    );

    const onMarginUpdate = (editedMarginLine: ScanReviewEditedMarginLine) => {
        marginLines.set(editedMarginLine.toothNumber, editedMarginLine.clone());
    };

    const onScanImageLoadCallback = (scanGeometry: THREE.BufferGeometry, scanImage: HTMLImageElement) => {
        const factory = () => {
            const heatmapRange = INITIAL_CLEARANCE_RANGE;
            const scanReviewRecord = new ScanReviewDcmBuilder(scanGeometry, scanImage)
                .buildDcmScanStoneMaterial()
                .buildDcmScanHeatmapMaterial(heatmapRange)
                .complete();
            return scanReviewRecord ?? null;
        };
        return factory;
    };

    React.useEffect(() => {
        const upperJawData = extractScanReviewDcmFileData(Jaw.UPPER, dcms);
        const lowerJawData = extractScanReviewDcmFileData(Jaw.LOWER, dcms);
        const computedViewState = computeInitialScanReviewViewStateFromDcm(upperJawData, lowerJawData);
        setInitialViewState(computedViewState);
        if (upperJawData) {
            loadScanReviewDcmFileData(upperJawData, onScanImageLoadCallback, setUpperJawFactory);
            marginLines.forEach(m => {
                if (AllUpperToothNumbers.find(unn => unn === m.toothNumber)) {
                    m.clear();
                }
            });
        }
        if (lowerJawData) {
            loadScanReviewDcmFileData(lowerJawData, onScanImageLoadCallback, setLowerJawFactory);
            marginLines.forEach(m => {
                if (AllLowerToothNumbers.find(unn => unn === m.toothNumber)) {
                    m.clear();
                }
            });
        }
    }, [dcms, marginLines]);

    const [marginMarking, setMarginMarking] = React.useState<boolean>(false);

    return (
        <StackY
            sx={{
                flex: 'auto',
                overflow: 'hidden',
                height: '100vh',
            }}
        >
            <File3dDropLoader scan={'Upper Jaw'} onDcmDrop={(files, event) => onDcmDrop(Jaw.UPPER, files, event)} />
            <File3dDropLoader scan={'Lower Jaw'} onDcmDrop={(files, event) => onDcmDrop(Jaw.LOWER, files, event)} />
            <ScanReviewAppProvider
                lowerJawFactory={lowerJawFactory}
                upperJawFactory={upperJawFactory}
                viewState={initialViewState}
                initialViewType={ScanReviewViewType.Complete}
                initialDisplayType={ScanReviewDisplayType.Scan}
                initialJawDisplayState={{ upper: true, lower: true }}
            >
                {marginMarking ? (
                    <ScanReviewMarginMarkingViewAppProvider
                        toothNumber={toothNumber}
                        marginLines={marginLines}
                        onMarginUpdate={onMarginUpdate}
                    >
                        <ScanReviewLandingPageMarginMarkingView
                            marginMarking={marginMarking}
                            setMarginMarking={setMarginMarking}
                            marginLines={marginLines}
                            preppedTeeth={preppedTeeth}
                            setPreppedTeeth={setPreppedTeeth}
                        />
                    </ScanReviewMarginMarkingViewAppProvider>
                ) : (
                    <ScanReviewCompleteViewAppProvider initialHeatmapRange={{ min: -0.1, max: 1.0 }}>
                        <ScanReviewLandingPageCompleteView
                            marginMarking={marginMarking}
                            setMarginMarking={setMarginMarking}
                        />
                    </ScanReviewCompleteViewAppProvider>
                )}
            </ScanReviewAppProvider>
        </StackY>
    );
};
